import { Eye, EyeOff } from 'lucide-react';
import { useId, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Field, TextInput } from '../../components/ui';
import { useT } from '../../i18n';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  ref?: Ref<HTMLInputElement>;
  label: string;
  hint?: ReactNode;
  error?: string | null;
  autoComplete: 'current-password' | 'new-password';
  /** Controlled visibility (e.g. one toggle for "new" + "repeat"). */
  visible?: boolean;
  onVisibleChange?: (v: boolean) => void;
  /** Hide the eye button (when a sibling field owns the toggle). */
  noToggle?: boolean;
};

/** Password field with a large show/hide button and a Caps Lock warning. */
export function PasswordInput({ ref, label, hint, error, visible, onVisibleChange, noToggle, id, className, onKeyUp, ...rest }: Props) {
  const t = useT();
  const autoId = useId();
  const inputId = id ?? autoId;
  const [ownVisible, setOwnVisible] = useState(false);
  const [caps, setCaps] = useState(false);
  const shown = visible ?? ownVisible;
  const toggle = () => {
    if (onVisibleChange) onVisibleChange(!shown);
    else setOwnVisible(!shown);
  };
  const keyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    setCaps(typeof e.getModifierState === 'function' && e.getModifierState('CapsLock'));
    onKeyUp?.(e);
  };
  return (
    <Field label={label} htmlFor={inputId} error={error} hint={caps ? <span className="font-semibold text-[var(--warn-text)]">{t('auth.password.caps')}</span> : hint}>
      <div className="relative">
        <TextInput
          ref={ref}
          id={inputId}
          type={shown ? 'text' : 'password'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          className={noToggle ? className : `pr-14 ${className ?? ''}`}
          onKeyUp={keyUp}
          {...rest}
        />
        {noToggle ? null : (
          <button
            type="button"
            onClick={toggle}
            aria-pressed={shown}
            aria-controls={inputId}
            aria-label={shown ? t('auth.password.hide') : t('auth.password.show')}
            title={shown ? t('auth.password.hide') : t('auth.password.show')}
            className="absolute top-1/2 right-1 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-lg text-[var(--ink-2)] hover:bg-[var(--accent-soft)]"
          >
            {shown ? <EyeOff className="size-6" aria-hidden /> : <Eye className="size-6" aria-hidden />}
          </button>
        )}
      </div>
    </Field>
  );
}
